/**
 * Shopify Customer API를 사용하여 고객 액세스 토큰의 유효성을 검증합니다.
 * @param token - 검증할 고객 액세스 토큰
 * @returns 토큰이 유효하면 true, 그렇지 않으면 false
 */
export async function validateCustomerToken(token: string): Promise<boolean> {
  try {
    const shopId = process.env.NEXT_PUBLIC_SHOPIFY_SHOP_ID;
    
    if (!shopId) {
      console.error('[Token Validation] Missing SHOPIFY_SHOP_ID environment variable');
      return false;
    }
    
    const customerApiUrl = `https://shopify.com/${shopId}/account/customer/api/2025-04/graphql`;
    
    const response = await fetch(customerApiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
      body: JSON.stringify({
        query: `
          query {
            customer {
              id
              emailAddress {
                emailAddress
              }
            }
          }
        `
      }),
    });

    if (!response.ok) {
      console.log('[Token Validation] HTTP error:', response.status, response.statusText);
      return false;
    }

    const data = await response.json();
    
    // 에러가 있거나 customer가 null이면 토큰이 유효하지 않음
    if (data.errors || !data.data?.customer) {
      console.log('[Token Validation] Token validation failed:', data.errors);
      return false;
    }
    
    console.log('[Token Validation] Token is valid for customer:', data.data.customer.id);
    return true;
  } catch (error) {
    console.error('[Token Validation] Token validation error:', error);
    return false;
  }
}

/**
 * 토큰 만료 시간을 확인합니다.
 * @returns 만료까지 남은 시간 (밀리초), 토큰이 없으면 null
 */
export function getTokenExpiryTime(): number | null {
  try {
    // 서버 사이드에서는 document 객체가 없으므로 null 반환
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return null;
    }

    const tokenExpiresAt = document.cookie
      .split('; ')
      .find(row => row.startsWith('token_expires_at='))
      ?.split('=')[1];

    if (!tokenExpiresAt) return null;

    const expiresAt = parseInt(tokenExpiresAt);
    const now = Date.now();
    return expiresAt - now;
  } catch (error) {
    console.error('[Token Expiry] Failed to get token expiry time:', error);
    return null;
  }
}

/**
 * 토큰이 곧 만료될 예정인지 확인합니다.
 * @param thresholdMinutes - 만료 임계값 (분), 기본값 15분
 * @returns 곧 만료될 예정이면 true
 */
export function isTokenExpiringSoon(thresholdMinutes: number = 15): boolean {
  const timeUntilExpiry = getTokenExpiryTime();
  if (timeUntilExpiry === null) return false;
  
  return timeUntilExpiry <= thresholdMinutes * 60 * 1000 && timeUntilExpiry > 0;
}

/**
 * 토큰이 만료되었는지 확인합니다.
 * @returns 만료되었으면 true
 */
export function isTokenExpired(): boolean {
  const timeUntilExpiry = getTokenExpiryTime();
  return timeUntilExpiry !== null && timeUntilExpiry <= 0;
}

/**
 * 예측적 토큰 갱신을 수행합니다.
 * @param thresholdMinutes - 갱신 임계값 (분), 기본값 15분
 * @returns 갱신 성공 시 true, 실패 시 false
 */
export async function proactiveTokenRefresh(thresholdMinutes: number = 15): Promise<boolean> {
  // 토큰이 곧 만료될 예정이고 아직 만료되지 않았을 때만 갱신
  if (isTokenExpiringSoon(thresholdMinutes) && !isTokenExpired()) {
    console.log(`[Proactive Refresh] Token expiring in ${thresholdMinutes} minutes, refreshing...`);
    return await attemptTokenRefresh();
  }
  
  return true; // 갱신이 필요하지 않으면 성공으로 간주
}

/**
 * 토큰 만료 시 자동 갱신을 시도합니다.
 * @returns 갱신 성공 시 true, 실패 시 false
 */
export async function attemptTokenRefresh(): Promise<boolean> {
  try {
    console.log('[Token Refresh] Attempting to refresh token...');
    
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });

    if (response.ok) {
      console.log('[Token Refresh] Token refreshed successfully');
      return true;
    } else {
      console.log('[Token Refresh] Token refresh failed:', response.status);
      return false;
    }
  } catch (error) {
    console.error('[Token Refresh] Token refresh error:', error);
    return false;
  }
}

/**
 * 토큰 만료 시 적절한 대응을 수행합니다.
 * @param onRefreshSuccess - 갱신 성공 시 콜백
 * @param onRefreshFailure - 갱신 실패 시 콜백
 * @param options - 추가 옵션
 */
export async function handleTokenExpiry(
  onRefreshSuccess?: () => void,
  onRefreshFailure?: () => void,
  options: {
    saveState?: boolean;
    stateData?: any;
    retryCount?: number;
    maxRetries?: number;
  } = {}
): Promise<boolean> {
  const { saveState = true, stateData = null, retryCount = 0, maxRetries = 2 } = options;
  
  // 최대 재시도 횟수 초과
  if (retryCount >= maxRetries) {
    console.log('[Token Expiry] Max retries exceeded');
    onRefreshFailure?.();
    return false;
  }

  // 작업 상태 저장
  if (saveState && stateData) {
    saveWorkState(stateData);
  }

  // 1. 자동 갱신 시도
  const refreshSuccess = await attemptTokenRefresh();
  
  if (refreshSuccess) {
    console.log('[Token Expiry] Token refreshed, continuing operation');
    onRefreshSuccess?.();
    return true;
  } else {
    console.log('[Token Expiry] Token refresh failed, user needs to re-login');
    onRefreshFailure?.();
    return false;
  }
}

/**
 * 현재 작업 상태를 저장합니다.
 */
export function saveWorkState(state: any): void {
  try {
    // 서버 사이드에서는 실행하지 않음
    if (typeof window === 'undefined') {
      return;
    }

    const workState = {
      ...state,
      timestamp: Date.now(),
      path: window.location.pathname,
      url: window.location.href
    };
    localStorage.setItem('workState', JSON.stringify(workState));
    console.log('[Work State] Work state saved');
  } catch (error) {
    console.error('[Work State] Failed to save work state:', error);
  }
}

/**
 * 저장된 작업 상태를 복원합니다.
 */
export function restoreWorkState(): any | null {
  try {
    // 서버 사이드에서는 null 반환
    if (typeof window === 'undefined') {
      return null;
    }

    const savedState = localStorage.getItem('workState');
    if (savedState) {
      const state = JSON.parse(savedState);
      const isExpired = Date.now() - state.timestamp > 30 * 60 * 1000; // 30분
      
      if (!isExpired) {
        console.log('[Work State] Work state restored');
        return state;
      } else {
        localStorage.removeItem('workState');
        console.log('[Work State] Work state expired, removed');
      }
    }
    return null;
  } catch (error) {
    console.error('[Work State] Failed to restore work state:', error);
    return null;
  }
}

/**
 * 작업 상태를 삭제합니다.
 */
export function clearWorkState(): void {
  try {
    // 서버 사이드에서는 실행하지 않음
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem('workState');
    console.log('[Work State] Work state cleared');
  } catch (error) {
    console.error('[Work State] Failed to clear work state:', error);
  }
}

/**
 * 토큰 관련 쿠키를 모두 삭제하는 헬퍼 함수
 * @param response - NextResponse 객체
 */
export function clearAuthCookies(response: any) {
  response.cookies.delete('shopify_access_token');
  response.cookies.delete('shopify_refresh_token');
  response.cookies.delete('token_expires_at');
  response.cookies.delete('shopify_user');
}

/**
 * 클라이언트 측에서 토큰 관련 쿠키를 삭제하는 함수
 */
export function clearAuthCookiesClient() {
  // 서버 사이드에서는 실행하지 않음
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  document.cookie = 'shopify_access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'shopify_refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'token_expires_at=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'shopify_user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
}

/**
 * 토큰 상태를 모니터링하는 함수
 * @returns 토큰 상태 정보
 */
export function getTokenStatus(): {
  hasToken: boolean;
  isExpired: boolean;
  isExpiringSoon: boolean;
  timeUntilExpiry: number | null;
  minutesUntilExpiry: number | null;
} {
  // 서버 사이드에서는 기본값 반환
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      hasToken: false,
      isExpired: false,
      isExpiringSoon: false,
      timeUntilExpiry: null,
      minutesUntilExpiry: null
    };
  }

  const timeUntilExpiry = getTokenExpiryTime();
  const hasToken = timeUntilExpiry !== null;
  const isExpired = hasToken && timeUntilExpiry <= 0;
  const isExpiringSoon = hasToken && timeUntilExpiry > 0 && timeUntilExpiry <= 15 * 60 * 1000;
  const minutesUntilExpiry = hasToken ? Math.ceil(timeUntilExpiry / 60000) : null;

  return {
    hasToken,
    isExpired,
    isExpiringSoon,
    timeUntilExpiry,
    minutesUntilExpiry
  };
} 